import type { RoastUploadMessage, UploadConfig } from '$lib/roasting/types';
import type { NewRoastResponse } from '$lib/types/RoastLog';
import axios, { type AxiosError } from 'axios';

/**
 * Custom error class for API errors when creating roasts
 */
export class CreateRoastApiError extends Error {
  newRoastId: string;
  status: number;

  constructor(message: string, newRoastId: string, status: number) {
    super(message || `API Error: ${status} ${message}`);
    this.name = 'CreateRoastApiError';
    this.newRoastId = newRoastId;
    this.status = status;
  }
}

import {
  dbPromise,
  DEFAULT_CONFIG,
  deleteFailedUpload,
  deletePendingUpload,
  getAllFailedUploads,
  getAllPendingUploads,
  getFailedUpload,
  getPendingUpload,
  moveToFailed as moveToFailedDB,
  storePendingUpload,
  updatePendingUpload
} from '$lib/services/indexedDB';

/**
 * Create a new roast by sending data to the server
 */
export async function createRoast({
  url,
  headers,
  newRoastData
}: RoastUploadMessage): Promise<NewRoastResponse> {
  try {
    const response = await axios.post(url, newRoastData, { headers });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      throw new CreateRoastApiError(
        axiosError.message,
        newRoastData.id,
        axiosError.response?.status || 500
      );
    }
    // Re-throw if it's not an axios error
    throw error;
  }
}

/**
 * Get all pending uploads from IndexedDB
 */
export async function getPendingUploads() {
  const db = await dbPromise;
  return getAllPendingUploads(db);
}

/**
 * Get all failed uploads from IndexedDB
 */
export async function getFailedUploads() {
  const db = await dbPromise;
  return getAllFailedUploads(db);
}

/**
 * Queue a new upload to be processed
 */
export async function queueUpload(
  uploadData: RoastUploadMessage,
  config: UploadConfig = DEFAULT_CONFIG
) {
  const db = await dbPromise;
  await storePendingUpload(db, uploadData);
  return uploadData.newRoastData.id;
}

/**
 * Retry a failed upload
 */
export async function retryFailedUpload(id: string) {
  const db = await dbPromise;
  const failedUpload = await getFailedUpload(db, id);

  if (!failedUpload) {
    throw new Error(`No failed upload found with id: ${id}`);
  }

  // Reset retry count and move back to pending uploads
  const pendingUpload = {
    ...failedUpload,
    retryCount: 0,
    timestamp: Date.now(),
    nextRetryTime: Date.now(),
    error: undefined // Clear previous error
  };

  // Move from failed to pending
  await db.put('pendingUploads', pendingUpload);
  await deleteFailedUpload(db, id);

  return id;
}

/**
 * Delete a failed upload
 */
export async function removeFailedUpload(id: string) {
  const db = await dbPromise;
  await deleteFailedUpload(db, id);
}

/**
 * Delete a pending upload
 */
export async function removePendingUpload(id: string) {
  const db = await dbPromise;
  await deletePendingUpload(db, id);
}

/**
 * Get a specific pending upload
 */
export async function getSinglePendingUpload(id: string) {
  const db = await dbPromise;
  return getPendingUpload(db, id);
}

/**
 * Get a specific failed upload
 */
export async function getSingleFailedUpload(id: string) {
  const db = await dbPromise;
  return getFailedUpload(db, id);
}

/**
 * Update a pending upload
 */
export async function updateUpload(id: string, updates: Partial<any>) {
  const db = await dbPromise;
  return updatePendingUpload(db, id, updates);
}

/**
 * Move an upload from pending to failed
 */
export async function moveToFailed(id: string, error: Error) {
  const db = await dbPromise;
  await moveToFailedDB(db, id, error);
}

/**
 * Re-export calculateBackoffDelay and DEFAULT_CONFIG from indexedDB
 */
export { calculateBackoffDelay, DEFAULT_CONFIG } from '../services/indexedDB';
